import { useState } from "react";
import type { FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useGymProfile } from "../hooks/useGymProfile";
import { useAuth } from "../auth/useAuth";
import { ApiError } from "../apiClient/gymClient";
import type { ProfileInput } from "../types/gym.types";

type FieldErrors = Partial<Record<keyof ProfileInput | "form", string>>;

// Light client-side pass for fast feedback. The server (validation.ts) is
// authoritative and re-checks everything, including mobile-number format.
function clientValidate(input: ProfileInput): FieldErrors {
  const errors: FieldErrors = {};
  if (input.fullName.trim().length < 2) errors.fullName = "Enter your full name.";
  if (!input.mobileNumber.trim()) errors.mobileNumber = "Enter your mobile number.";
  if (input.sex !== "male" && input.sex !== "female") errors.sex = "Select male or female.";
  return errors;
}

export function ProfileForm() {
  const { profile, save } = useGymProfile();
  const { principal } = useAuth();
  const navigate = useNavigate();
  const isEditing = profile !== null;

  const [fullName, setFullName] = useState(profile?.fullName ?? "");
  const [mobileNumber, setMobileNumber] = useState(profile?.mobileNumber ?? "");
  const [sex, setSex] = useState<"male" | "female" | "">(profile?.sex ?? "");
  const [medicalConditions, setMedicalConditions] = useState(profile?.medicalConditions ?? "");

  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [savedMessage, setSavedMessage] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setSavedMessage(null);

    const input: ProfileInput = {
      fullName: fullName.trim(),
      mobileNumber: mobileNumber.trim(),
      sex: sex as "male" | "female",
      medicalConditions: medicalConditions.trim() || undefined,
    };

    const clientErrors = clientValidate(input);
    if (Object.keys(clientErrors).length > 0) {
      setErrors(clientErrors);
      return;
    }

    setErrors({});
    setSubmitting(true);
    try {
      await save(input);
      if (isEditing) {
        setSavedMessage("Profile updated.");
      } else {
        // First-time setup complete — send them to the schedule.
        navigate("/gym", { replace: true });
      }
    } catch (err) {
      if (err instanceof ApiError && err.fields) {
        setErrors(err.fields as FieldErrors);
      } else {
        setErrors({ form: err instanceof Error ? err.message : "Could not save your profile." });
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <form className="form" onSubmit={handleSubmit} noValidate>
      {errors.form && <div className="form-banner form-banner--error">{errors.form}</div>}
      {savedMessage && <div className="form-banner form-banner--success">{savedMessage}</div>}

      <div className="form-field">
        <label htmlFor="workEmail">Work email</label>
        <input id="workEmail" type="email" value={principal?.userDetails ?? ""} readOnly disabled />
        <span className="field-hint">From your sign-in — can&rsquo;t be changed here.</span>
      </div>

      <div className="form-field">
        <label htmlFor="fullName">Full name</label>
        <input
          id="fullName"
          type="text"
          value={fullName}
          onChange={(e) => setFullName(e.target.value)}
          aria-invalid={Boolean(errors.fullName)}
          autoComplete="name"
        />
        {errors.fullName && <span className="field-error">{errors.fullName}</span>}
      </div>

      <div className="form-field">
        <label htmlFor="mobileNumber">Mobile number</label>
        <input
          id="mobileNumber"
          type="tel"
          value={mobileNumber}
          onChange={(e) => setMobileNumber(e.target.value)}
          aria-invalid={Boolean(errors.mobileNumber)}
          placeholder="01xxxxxxxxx"
          autoComplete="tel"
        />
        <span className="field-hint">Used for reminders and no-show warnings.</span>
        {errors.mobileNumber && <span className="field-error">{errors.mobileNumber}</span>}
      </div>

      <fieldset className="form-field">
        <legend>Sex</legend>
        <div className="radio-row">
          <label className="radio">
            <input
              type="radio"
              name="sex"
              value="male"
              checked={sex === "male"}
              onChange={() => setSex("male")}
            />
            Male
          </label>
          <label className="radio">
            <input
              type="radio"
              name="sex"
              value="female"
              checked={sex === "female"}
              onChange={() => setSex("female")}
            />
            Female
          </label>
        </div>
        {errors.sex && <span className="field-error">{errors.sex}</span>}
      </fieldset>

      <div className="form-field">
        <label htmlFor="medicalConditions">Medical conditions (optional)</label>
        <textarea
          id="medicalConditions"
          rows={3}
          value={medicalConditions}
          onChange={(e) => setMedicalConditions(e.target.value)}
          aria-invalid={Boolean(errors.medicalConditions)}
          placeholder="Anything the coach should know about"
        />
        {errors.medicalConditions && <span className="field-error">{errors.medicalConditions}</span>}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn btn-primary" disabled={submitting}>
          {submitting ? "Saving…" : isEditing ? "Save changes" : "Complete profile"}
        </button>
      </div>
    </form>
  );
}
